"use client";

import React from "react";
import { ShieldCheck, Sun, Clock, Award, CircleCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const trustBadges = [{
  icon: ShieldCheck,
  title: "FDACS Licensed",
  description: "Fully licensed experts with deep knowledge of Port Charlotte's specific building codes and pest regulations."
}, {
  icon: Sun,
  title: "Climate Specialists",
  description: "Treatments engineered for the unique humidity levels found along the Peace River and Charlotte Harbor."
}, {
  icon: Clock,
  title: "Always Available",
  description: "Reliable, local scheduling with rapid response times for every Port Charlotte neighborhood."
}, {
  icon: Award,
  title: "Guaranteed Results",
  description: "Proven protection for canal-front homes and suburban estates with a 100% satisfaction guarantee."
}];
const benefits = ["Comprehensive residential and commercial pest solutions", "Eco-friendly and pet-safe treatment options available", "Transparent, upfront pricing with absolutely no hidden fees", "$35/month affordable service plans", "24/7 emergency service in Port Charlotte"];
export default function WhyPortCharlotteResidentsChooseUs() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="why-port-charlotte-residents-choose-us" className="py-16 md:py-24 bg-background text-foreground overflow-hidden" ref={sectionRef}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {}
          <div className={`flex flex-col gap-6 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="space-y-4">
              <Badge variant="secondary" className="px-3 py-1 text-sm">
                Local Expertise
              </Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default tracking-tight text-foreground">
                Why Port Charlotte Residents Choose Us
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                As a locally owned and operated team, we understand that Port Charlotte's unique geography—from the vast expanse of Charlotte Harbor to our intricate canal systems—requires a specialized approach to pest management. The suburban layout of our community, interspersed with waterways and lush Florida landscapes, creates specific corridors for pest activity that generic services often overlook. We pride ourselves on reliable scheduling and deep local expertise, ensuring that whether you live in a waterfront property or a quiet suburban block, your home receives protection tailored to our specific environment. Our commitment to Port Charlotte residents is built on trust, punctuality, and a thorough understanding of how our local climate drives seasonal pest pressures.
              </p>
            </div>

            <ul className="space-y-4 mt-2">
              {benefits.map((benefit, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                  <CircleCheck className="size-6 text-primary shrink-0 mt-0.5" />
                  <span className="text-foreground font-medium">{benefit}</span>
                </li>)}
            </ul>

            <div className="pt-4">
              <Button asChild size="lg" className="group">
                <Link to="/ContactPage">
                  Get a Free Inspection
                  <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className={`grid sm:grid-cols-2 gap-4 md:gap-6 transition-all duration-700 delay-200 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            {trustBadges.map((badge, i) => {
            const Icon = badge.icon;
            return <Card key={i} data-index={i} className="bg-card border-border shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col items-center text-center p-6 gap-4">
                  <div className="p-4 bg-primary/10 rounded-full text-primary mb-2">
                    <Icon className="size-8" />
                  </div>
                  <div>
                    <h3 className="font-default font-semibold text-xl text-card-foreground mb-2">
                      {badge.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {badge.description}
                    </p>
                  </div>
                </Card>;
          })}
          </div>

        </div>
      </div>
    </section>;
}